import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AdvancedMarker,
  CollisionBehavior,
  Map as GoogleMap,
} from "@vis.gl/react-google-maps";
import {
  Info,
  Layers,
  Pause,
  Route,
  SquareMousePointer,
  StickyNote,
  Warehouse,
} from "lucide-react";

import {
  type CarrierDispatchCenter,
  type CarrierOperator,
  type CarrierTask,
  type CarrierTrip,
  useCarrierDispatchCenters,
} from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { isTaskOnCancellationHold } from "@/lib/cancellation-work-hold";
import { taskHasVisibleNote } from "@/lib/task-instructions";
import { stopStatus } from "./trip-stops";
import { centerLatLng } from "./center-location";
import { LANE_META, LANE_ORDER } from "./task-type-meta";
import { TasksSelector } from "./tasks-selector";
import {
  isTaskSelectableForStaging,
  taskDispatchCenterId,
} from "./task-staging";
import {
  hashColor,
  isOverdue,
  operatorInitials,
  operatorName,
  taskCounts,
  tripLabel,
} from "./dispatch-presenters";
import { operatorLocationFreshness } from "./dispatch-live-state";

const DEFAULT_CENTER = { lat: 43.6532, lng: -79.3832 };
const isMapKeyConfigured = () =>
  Boolean(import.meta.env.VITE_GOOGLE_MAPS_API_KEY);

type MarkerKind = "tasks" | "trips" | "drivers";
type MarkerCountChange = (kind: MarkerKind, delta: 1 | -1) => void;

function MarkerMountReporter({
  children,
  kind,
  onMarkerCountChange,
}: {
  children: React.ReactNode;
  kind: MarkerKind;
  onMarkerCountChange: MarkerCountChange;
}) {
  useEffect(() => {
    onMarkerCountChange(kind, 1);
    return () => onMarkerCountChange(kind, -1);
  }, [kind, onMarkerCountChange]);

  return children;
}

function TaskMapMarker({
  tasks,
  onClick,
  selected,
  unavailable,
}: {
  tasks: CarrierTask[];
  onClick: () => void;
  selected: boolean;
  unavailable: boolean;
}) {
  const counts = taskCounts(tasks);
  const lanes = LANE_ORDER.filter((lane) => counts[lane] > 0);
  const overdue = tasks.some((task) => isOverdue(task));
  const noted = tasks.some(taskHasVisibleNote);
  const heldCount = tasks.filter(isTaskOnCancellationHold).length;
  const allHeld = heldCount === tasks.length;

  return (
    <button
      type="button"
      aria-label={`${allHeld ? "Cancellation hold" : selected ? "Remove" : "Stage"} ${tasks.length} task${tasks.length === 1 ? "" : "s"}`}
      aria-pressed={selected}
      disabled={unavailable}
      className={cn(
        "flex min-h-11 min-w-11 flex-col items-center justify-center focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-35 lg:min-h-0 lg:min-w-0",
        selected && "drop-shadow-[0_0_5px_rgba(79,70,229,0.8)]"
      )}
      onClick={onClick}
    >
      <div
        className={cn(
          "relative flex min-h-7 overflow-hidden rounded-lg border-2 border-zinc-950 shadow-lg",
          overdue && "ring-2 ring-red-500 ring-offset-2"
        )}
      >
        {allHeld ? (
          <span className="grid min-w-8 place-items-center bg-zinc-400 px-1 text-white">
            <Pause className="h-3.5 w-3.5" />
          </span>
        ) : (
          lanes.map((lane) => (
            <span
              key={lane}
              className="grid min-w-7 place-items-center px-1 text-xs font-bold text-white"
              style={{ background: LANE_META[lane].color }}
            >
              {counts[lane]}
            </span>
          ))
        )}
        {heldCount > 0 && !allHeld && (
          <span
            title={`${heldCount} task${heldCount === 1 ? "" : "s"} on cancellation hold`}
            className="grid min-w-5 place-items-center bg-zinc-200 px-0.5 text-zinc-700"
          >
            <Pause className="h-2.5 w-2.5" />
          </span>
        )}
        {noted && (
          <span
            title="Carries notes"
            className="grid min-w-5 place-items-center bg-white px-0.5 text-zinc-900"
          >
            <StickyNote className="h-2.5 w-2.5" />
          </span>
        )}
      </div>
      <span className="h-2 w-0.5 bg-zinc-950" />
      <span className="h-1.5 w-1.5 rounded-full border border-zinc-950 bg-white" />
    </button>
  );
}

export function TripStopMapMarker({
  trip,
  sequence,
  tasks,
  onClick,
}: {
  trip: CarrierTrip;
  sequence: number;
  tasks: NonNullable<CarrierTrip["waypoints"]>[number]["tasks"];
  onClick: () => void;
}) {
  const heldCount = (tasks ?? []).filter(isTaskOnCancellationHold).length;
  const allHeld = heldCount > 0 && heldCount === (tasks?.length ?? 0);
  const resolved = stopStatus(tasks ?? []) === "done";
  return (
    <button
      type="button"
      title={
        resolved
          ? "Resolved stop: all tasks completed, failed, or cancelled"
          : undefined
      }
      aria-label={`${tripLabel(trip)} stop ${sequence}${resolved ? ", resolved" : ""}${heldCount ? `, ${heldCount} task${heldCount === 1 ? "" : "s"} on cancellation hold` : ""}`}
      className="flex min-h-11 min-w-11 flex-col items-center justify-center focus:outline-hidden lg:min-h-0 lg:min-w-0"
      onClick={onClick}
    >
      <span
        className="grid h-7 w-7 place-items-center rounded-lg border-2 border-zinc-950 text-xs font-bold text-white shadow-lg"
        style={{
          background: allHeld
            ? "#a1a1aa"
            : resolved
              ? "#94a3b8"
              : (trip.color ?? "#4f46e5"),
          borderColor: resolved ? (trip.color ?? "#4f46e5") : undefined,
        }}
      >
        {allHeld ? <Pause className="h-3.5 w-3.5" /> : sequence}
      </span>
      {heldCount > 0 && !allHeld && (
        <span
          title={`${heldCount} task${heldCount === 1 ? "" : "s"} on cancellation hold`}
          className="-mt-1 grid h-4 w-4 place-items-center rounded-full border border-white bg-zinc-300 text-zinc-700 shadow"
        >
          <Pause className="h-2.5 w-2.5" />
        </span>
      )}
      <span className="h-2 w-0.5 bg-zinc-950" />
      <span className="h-1.5 w-1.5 rounded-full border border-zinc-950 bg-white" />
    </button>
  );
}

function CenterMapMarker({ name }: { name: string }) {
  return (
    <span
      title={name}
      className="grid h-6 w-6 place-items-center rounded-md border-2 border-white bg-zinc-900 text-white shadow-lg"
    >
      <Warehouse className="h-3 w-3" />
    </span>
  );
}

function OperatorMapMarker({ op }: { op: CarrierOperator }) {
  const freshness = operatorLocationFreshness(op);
  const name = operatorName(op);
  const locationLabel = `${freshness} location`;
  return (
    <button
      type="button"
      aria-label={`Operator ${name}, ${locationLabel}`}
      className="relative grid h-11 w-11 place-items-center rounded-full border-2 border-white text-xs font-bold text-white shadow-lg focus:outline-hidden lg:h-9 lg:w-9"
      style={{ background: hashColor(op.id) }}
      title={`${name} · ${locationLabel}`}
    >
      {operatorInitials(op)}
      <span
        className={cn(
          "absolute -left-1 -top-1 h-2.5 w-2.5 rounded-full border border-white",
          freshness === "fresh" && "animate-pulse bg-emerald-500",
          freshness === "stale" && "bg-amber-500",
          freshness === "offline" && "bg-zinc-400"
        )}
      />
      {op.vehicle && (
        <span className="absolute -bottom-1 -right-1 rounded-full border border-white bg-white px-1 text-[9px] font-bold text-zinc-700">
          V
        </span>
      )}
    </button>
  );
}

function MapMarkers({
  tasks,
  trips,
  operators,
  onAssignTasks,
  onRemoveTasks,
  onOpenTrip,
  centers,
  onMarkerCountChange,
  stagedTaskIds,
  stagingCenterId,
}: {
  tasks: CarrierTask[];
  trips: CarrierTrip[];
  operators: CarrierOperator[];
  centers: CarrierDispatchCenter[];
  onAssignTasks: (tasks: CarrierTask[]) => void;
  onRemoveTasks: (taskIds: string[]) => void;
  onOpenTrip: (trip: CarrierTrip) => void;
  onMarkerCountChange: MarkerCountChange;
  stagedTaskIds: Set<string>;
  stagingCenterId: string | null;
}) {
  const taskClusters = useMemo(() => {
    const groups = new Map<
      string,
      { position: google.maps.LatLngLiteral; tasks: CarrierTask[] }
    >();
    for (const task of tasks) {
      if (task.tripId) continue;
      const position = task.mapLocation;
      if (!position) continue;
      const key = `${position.lat},${position.lng}`;
      const existing = groups.get(key);
      if (existing) existing.tasks.push(task);
      else groups.set(key, { position, tasks: [task] });
    }
    return Array.from(groups.values());
  }, [tasks]);

  const tripStops = useMemo(
    () =>
      trips.flatMap((trip) =>
        (trip.waypoints ?? []).flatMap((waypoint) => {
          if (!waypoint.mapLocation) return [];
          return [
            {
              id: waypoint.id,
              trip,
              sequence: waypoint.sequence,
              tasks: waypoint.tasks,
              hasHeldTasks: (waypoint.tasks ?? []).some(
                isTaskOnCancellationHold
              ),
              position: waypoint.mapLocation,
            },
          ];
        })
      ),
    [trips]
  );

  const operatorMarkers = useMemo(
    () =>
      operators.flatMap((op) => {
        const position = op.mapLocation;
        return position ? [{ op, position }] : [];
      }),
    [operators]
  );
  const centerMarkers = useMemo(
    () =>
      centers.flatMap((center) => {
        const position = centerLatLng(center);
        return position ? [{ center, position }] : [];
      }),
    [centers]
  );

  return (
    <>
      {taskClusters.map((cluster) => {
        const clusterCenterId =
          stagingCenterId ??
          taskDispatchCenterId(
            cluster.tasks.find(isTaskSelectableForStaging) ?? cluster.tasks[0]
          );
        const selectableTasks = cluster.tasks.filter(
          (task) =>
            isTaskSelectableForStaging(task) &&
            taskDispatchCenterId(task) === clusterCenterId
        );
        const selected =
          selectableTasks.length > 0 &&
          selectableTasks.every((task) => stagedTaskIds.has(task.id));
        return (
          <AdvancedMarker
            key={`tasks-${cluster.position.lat},${cluster.position.lng}`}
            position={cluster.position}
          >
            <MarkerMountReporter
              kind="tasks"
              onMarkerCountChange={onMarkerCountChange}
            >
              <TaskMapMarker
                tasks={cluster.tasks}
                selected={selected}
                unavailable={selectableTasks.length === 0}
                onClick={() =>
                  selected
                    ? onRemoveTasks(selectableTasks.map((task) => task.id))
                    : onAssignTasks(selectableTasks)
                }
              />
            </MarkerMountReporter>
          </AdvancedMarker>
        );
      })}
      {tripStops.map((stop) => (
        <AdvancedMarker
          key={`stop-${stop.id}`}
          position={stop.position}
          collisionBehavior={
            stop.hasHeldTasks
              ? CollisionBehavior.REQUIRED_AND_HIDES_OPTIONAL
              : undefined
          }
          zIndex={
            stop.hasHeldTasks ? 10000 + (stop.trip.tripNumber ?? 0) : undefined
          }
        >
          <MarkerMountReporter
            kind="trips"
            onMarkerCountChange={onMarkerCountChange}
          >
            <TripStopMapMarker
              trip={stop.trip}
              sequence={stop.sequence}
              tasks={stop.tasks}
              onClick={() => onOpenTrip(stop.trip)}
            />
          </MarkerMountReporter>
        </AdvancedMarker>
      ))}
      {centerMarkers.map(({ center, position }) => (
        <AdvancedMarker key={`center-${center.id}`} position={position}>
          <CenterMapMarker name={center.name} />
        </AdvancedMarker>
      ))}
      {operatorMarkers.map(({ op, position }) => (
        <AdvancedMarker key={`operator-${op.id}`} position={position}>
          <MarkerMountReporter
            kind="drivers"
            onMarkerCountChange={onMarkerCountChange}
          >
            <OperatorMapMarker op={op} />
          </MarkerMountReporter>
        </AdvancedMarker>
      ))}
    </>
  );
}

export function MapShell({
  tasks,
  trips,
  operators,
  onAssignTasks,
  onRemoveTasks,
  onOpenTrip,
  stagedTasks,
  stagingCenterId,
  selectionMode,
  onToggleSelectionMode,
  onBoxSelection,
  allowBoxSelection = true,
  compact = false,
}: {
  tasks: CarrierTask[];
  trips: CarrierTrip[];
  operators: CarrierOperator[];
  onAssignTasks: (tasks: CarrierTask[]) => void;
  onRemoveTasks: (taskIds: string[]) => void;
  onOpenTrip: (trip: CarrierTrip) => void;
  stagedTasks: CarrierTask[];
  stagingCenterId: string | null;
  selectionMode: boolean;
  onToggleSelectionMode: () => void;
  onBoxSelection: (tasks: CarrierTask[]) => void;
  allowBoxSelection?: boolean;
  compact?: boolean;
}) {
  const keyConfigured = isMapKeyConfigured();
  const [layers, setLayers] = useState({
    tasks: true,
    trips: true,
    drivers: true,
    centers: true,
  });
  const [legendOpen, setLegendOpen] = useState(false);
  const dispatchCenters = useCarrierDispatchCenters();
  const [mountedMarkerCounts, setMountedMarkerCounts] = useState({
    tasks: 0,
    trips: 0,
    drivers: 0,
  });
  const handleMarkerCountChange = useCallback<MarkerCountChange>(
    (kind, delta) => {
      setMountedMarkerCounts((current) => ({
        ...current,
        [kind]: Math.max(0, current[kind] + delta),
      }));
    },
    []
  );

  const toggleLayer = (layer: keyof typeof layers) => {
    setLayers((current) => ({ ...current, [layer]: !current[layer] }));
  };

  return (
    <main
      aria-label="Dispatch map workspace"
      className="relative min-w-0 flex-1 bg-[#d8eef4]"
      data-dispatch-shortcuts
    >
      {keyConfigured && (
        <div className="absolute inset-0 z-0" data-testid="dispatch-google-map">
          <GoogleMap
            defaultCenter={DEFAULT_CENTER}
            defaultZoom={13}
            gestureHandling="greedy"
            disableDefaultUI
            mapId="3667d2e0a1e93d2b"
            style={{ width: "100%", height: "100%" }}
          >
            <MapMarkers
              tasks={layers.tasks ? tasks : []}
              trips={layers.trips ? trips : []}
              operators={layers.drivers ? operators : []}
              centers={layers.centers ? (dispatchCenters.data ?? []) : []}
              onAssignTasks={onAssignTasks}
              onRemoveTasks={onRemoveTasks}
              onOpenTrip={onOpenTrip}
              onMarkerCountChange={handleMarkerCountChange}
              stagedTaskIds={new Set(stagedTasks.map((task) => task.id))}
              stagingCenterId={stagingCenterId}
            />
            {allowBoxSelection && selectionMode && (
              <TasksSelector
                tasks={tasks}
                onSelectionComplete={onBoxSelection}
              />
            )}
          </GoogleMap>
        </div>
      )}

      {allowBoxSelection && (
        <div className="absolute left-3 top-3 z-20 flex items-center gap-1 rounded-xl bg-white/95 p-1 shadow-sm">
          <Button
            type="button"
            variant={selectionMode ? "default" : "ghost"}
            size="sm"
            className="gap-2"
            aria-pressed={selectionMode}
            onClick={onToggleSelectionMode}
          >
            <SquareMousePointer className="h-4 w-4" />
            Box-select
            <kbd className="rounded bg-zinc-100 px-1 text-[10px] font-bold text-zinc-500">
              S
            </kbd>
          </Button>
        </div>
      )}

      <div
        className={cn(
          "absolute right-3 top-3 z-20 rounded-xl bg-white/95 shadow-sm",
          compact ? "max-w-[calc(100%-1.5rem)] p-1.5" : "w-64 p-3"
        )}
      >
        <div
          className={cn(
            "flex items-center gap-2 text-sm font-semibold text-zinc-800",
            compact ? "sr-only" : "mb-3"
          )}
        >
          <Layers className="h-4 w-4" />
          Layers
        </div>
        <div className={cn("grid grid-cols-3 gap-1", !compact && "mb-2")}>
          {(["tasks", "trips", "drivers", "centers"] as const).map((layer) => (
            <Button
              key={layer}
              type="button"
              variant={layers[layer] ? "secondary" : "ghost"}
              size="sm"
              aria-pressed={layers[layer]}
              onClick={() => toggleLayer(layer)}
              className="h-11 px-2 text-[11px] capitalize lg:h-7"
            >
              {layer}
            </Button>
          ))}
        </div>
        <div className={cn(compact && "hidden")}>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-expanded={legendOpen}
            className="h-7 w-full justify-start gap-2 px-1 text-[12px] font-semibold text-zinc-700"
            onClick={() => setLegendOpen((open) => !open)}
          >
            <Info className="h-3.5 w-3.5" />
            Legend
          </Button>
          {legendOpen && (
            <div className="px-1 pb-1 pt-1.5">
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
                {LANE_ORDER.map((lane) => (
                  <span key={lane} className="flex items-center gap-2">
                    <span
                      className="h-2.5 w-2.5 rounded-[3px]"
                      style={{ background: LANE_META[lane].color }}
                    />
                    <span className="text-[12px] text-zinc-700">
                      {LANE_META[lane].label}
                    </span>
                  </span>
                ))}
              </div>
              <div className="my-2 h-px bg-zinc-200" />
              <div className="space-y-1.5">
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 rounded-[4px] bg-slate-400" />
                  <span className="text-[12px] text-zinc-700">
                    Resolved stop (completed, failed, or cancelled)
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="grid h-4 w-4 place-items-center rounded-[4px] border border-zinc-900 bg-white text-zinc-900">
                    <StickyNote className="h-2.5 w-2.5" />
                  </span>
                  <span className="text-[12px] text-zinc-700">
                    Carries notes
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 rounded-[4px] bg-white ring-2 ring-red-500" />
                  <span className="text-[12px] text-zinc-700">Overdue</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="grid h-4 w-4 place-items-center rounded-[4px] bg-zinc-900 text-white">
                    <Warehouse className="h-2.5 w-2.5" />
                  </span>
                  <span className="text-[12px] text-zinc-700">
                    Dispatch center
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 rounded-full border-2 border-white bg-zinc-400 shadow" />
                  <span className="text-[12px] text-zinc-700">
                    Driver, numbered stops follow trip order
                  </span>
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {!keyConfigured && (
        <div className="flex h-full items-center justify-center p-6 text-center">
          <div className="max-w-sm rounded-xl border border-zinc-200 bg-white/95 p-5 shadow-lg">
            <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
              <Route className="h-5 w-5" />
            </div>
            <h2 className="text-base font-semibold text-zinc-900">
              Dispatch console
            </h2>
            <p className="mt-2 text-sm text-zinc-600">
              VITE_GOOGLE_MAPS_API_KEY is not configured.
            </p>
            <p className="mt-2 text-xs text-zinc-500">
              Fleet rail, KPI strip, assign panel, lookup, and the marker layer
              are wired to carrier-scoped data.
            </p>
          </div>
        </div>
      )}

      {keyConfigured && (
        <div
          className={cn(
            "pointer-events-none absolute left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-full bg-white/95 px-3 py-1 text-xs font-medium text-zinc-600 shadow-sm",
            compact
              ? "bottom-20 max-w-[calc(100%-1.5rem)] truncate"
              : "bottom-3"
          )}
        >
          {mountedMarkerCounts.tasks} task marker
          {mountedMarkerCounts.tasks === 1 ? "" : "s"} ·{" "}
          {mountedMarkerCounts.trips} trip stop
          {mountedMarkerCounts.trips === 1 ? "" : "s"} ·{" "}
          {mountedMarkerCounts.drivers} driver marker
          {mountedMarkerCounts.drivers === 1 ? "" : "s"}
        </div>
      )}
    </main>
  );
}
